/** Sentinel passed as `defaultValue` so a missing key is never shown raw. */
export const GUIDE_COPY_MISSING = '\u0000missing';

const UNFILLED_PLACEHOLDER = /\{\{\s*[\w.]+\s*\}\}/;

/** Notice keys in the `assistant` namespace. Step copy stays in `guides`. */
export const GUIDE_NOTICE = {
  narrowScreen: 'guide.narrowScreen',
  anchorNotFound: 'guide.anchorNotFound',
  stopped: 'guide.stopped',
  finished: 'guide.finished',
  missingText: 'guide.missingText',
} as const;

export type GuideTranslator = (
  key: string,
  options: { defaultValue: string; replace: Record<string, string | number | boolean> },
) => unknown;

/**
 * A translated guide line, or `undefined` when the key is missing, the
 * namespace is not loaded, or a placeholder was left unfilled. Never the raw key.
 */
export function acceptedGuideCopy(rendered: unknown, key: string): string | undefined {
  if (typeof rendered !== 'string') return undefined;
  if (rendered.length === 0) return undefined;
  if (rendered === GUIDE_COPY_MISSING || rendered === key) return undefined;
  if (rendered === `guides:${key}` || rendered === `assistant:${key}`) return undefined;
  if (UNFILLED_PLACEHOLDER.test(rendered)) return undefined;
  return rendered;
}

export function translateGuideKey(
  t: GuideTranslator,
  key: string,
  params?: Record<string, string | number | boolean>,
): string | undefined {
  return acceptedGuideCopy(
    t(key, { defaultValue: GUIDE_COPY_MISSING, replace: params ?? {} }),
    key,
  );
}

/** Primitive context values a guide line may interpolate. Objects are dropped. */
export function guideParamsFromContext(
  ctx: Record<string, unknown>,
): Record<string, string | number | boolean> | undefined {
  const params: Record<string, string | number | boolean> = {};
  let any = false;
  for (const [key, value] of Object.entries(ctx)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      params[key] = value;
      any = true;
    }
  }
  return any ? params : undefined;
}
